import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  ExternalLink,
  Star,
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  Building2,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import type { VendorWithReviews } from "@shared/schema";

const CATEGORIES = [
  "All",
  "DMS",
  "CRM",
  "Advertising",
  "Inventory",
  "Finance",
  "Insurance",
  "Reconditioning",
  "Photography",
  "Compliance",
  "Other",
];

const SORT_OPTIONS = [
  { value: "composite", label: "Overall Score" },
  { value: "affordability", label: "Affordability" },
  { value: "technology", label: "Technology" },
  { value: "value", label: "Value" },
  { value: "support", label: "Support" },
  { value: "sizeFit", label: "Size Fit" },
];

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-muted-foreground shrink-0">{label}</span>
      <div className="flex-1 bg-muted rounded-full h-1.5">
        <div
          className="bg-primary h-1.5 rounded-full"
          style={{ width: `${(value / 10) * 100}%` }}
        />
      </div>
      <span className="w-6 text-right font-medium">{value}</span>
    </div>
  );
}

function StarRating({ value, onChange, readonly = false }: { value: number; onChange?: (v: number) => void; readonly?: boolean }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          data-testid={readonly ? undefined : `star-${s}`}
          className={`w-4 h-4 ${readonly ? "" : "cursor-pointer"} transition-colors ${
            s <= (hover || value) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
          }`}
          onClick={() => !readonly && onChange?.(s)}
          onMouseEnter={() => !readonly && setHover(s)}
          onMouseLeave={() => !readonly && setHover(0)}
        />
      ))}
    </div>
  );
}

function ReviewForm({ vendorId, existingReview, onClose }: {
  vendorId: string;
  existingReview?: { stars: number; comment: string | null } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const [stars, setStars] = useState(existingReview?.stars ?? 0);
  const [comment, setComment] = useState(existingReview?.comment ?? "");
  const { toast } = useToast();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/vendors/${vendorId}/reviews`, { stars, comment }),
    onSuccess: () => {
      toast({ title: t("pages:vendors.review.saved") });
      qc.invalidateQueries({ queryKey: ["/api/vendors"] });
      qc.invalidateQueries({ queryKey: ["/api/vendors", vendorId] });
      onClose();
    },
    onError: (e: any) => toast({ title: t("pages:vendors.review.failedSave"), description: e.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-3">
      <div>
        <Label>{t("pages:vendors.review.yourRating")}</Label>
        <div className="mt-1">
          <StarRating value={stars} onChange={setStars} />
        </div>
      </div>
      <div>
        <Label htmlFor="review-comment">{t("pages:vendors.review.commentOptional")}</Label>
        <Textarea
          id="review-comment"
          data-testid="input-review-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("pages:vendors.review.commentPlaceholder")}
          rows={3}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose} data-testid="button-cancel-review">{t("common:cancel")}</Button>
        <Button
          onClick={() => mutation.mutate()}
          disabled={stars === 0 || mutation.isPending}
          data-testid="button-submit-review"
        >
          {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {existingReview ? t("pages:vendors.review.updateReview") : t("pages:vendors.review.submitReview")}
        </Button>
      </div>
    </div>
  );
}

function AddVendorDialog({ onAdded }: { onAdded: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [description, setDescription] = useState("");
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/vendors", {
        name: name.trim(),
        category,
        websiteUrl: websiteUrl.trim() || null,
        description: description.trim() || null,
      });
      return res.json() as Promise<{ id: string; name: string; category: string }>;
    },
    onSuccess: (vendor: any) => {
      qc.invalidateQueries({ queryKey: ["/api/vendors"] });
      if (vendor.enrichmentError) {
        toast({
          title: t("pages:vendors.add.vendorAdded"),
          description: t("pages:vendors.add.vendorAddedNoAI"),
        });
      } else {
        toast({ title: t("pages:vendors.add.vendorAddedTitle"), description: t("pages:vendors.add.vendorAddedDesc") });
      }
      setOpen(false);
      setName(""); setCategory(""); setWebsiteUrl(""); setDescription("");
      onAdded(vendor.id);
    },
    onError: (e: any) => toast({ title: t("pages:vendors.add.failedSubmit"), description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="button-add-vendor">
          <Plus className="w-4 h-4 mr-2" />
          {t("pages:vendors.add.addVendor")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("pages:vendors.add.submitVendorTitle")}</DialogTitle>
        </DialogHeader>
        {submitMutation.isPending ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground text-center">
              {t("pages:vendors.add.generatingAI")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="vendor-name">{t("pages:vendors.add.vendorNameLabel")}</Label>
              <Input
                id="vendor-name"
                data-testid="input-vendor-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("pages:vendors.add.vendorNamePlaceholder")}
              />
            </div>
            <div>
              <Label htmlFor="vendor-category">{t("pages:vendors.add.categoryLabel")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="vendor-category" data-testid="select-vendor-category">
                  <SelectValue placeholder={t("pages:vendors.add.selectCategory")} />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter((c) => c !== "All").map((c) => (
                    <SelectItem key={c} value={c}>{t(`pages:vendors.categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="vendor-url">{t("pages:vendors.add.websiteUrlLabel")}</Label>
              <Input
                id="vendor-url"
                data-testid="input-vendor-url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder={t("pages:vendors.add.urlPlaceholder")}
              />
            </div>
            <div>
              <Label htmlFor="vendor-description">{t("pages:vendors.add.descriptionLabel")}</Label>
              <Textarea
                id="vendor-description"
                data-testid="input-vendor-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("pages:vendors.add.descriptionPlaceholder")}
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="button-cancel-add-vendor">{t("common:cancel")}</Button>
              <Button
                onClick={() => submitMutation.mutate()}
                disabled={!name.trim() || !category}
                data-testid="button-submit-add-vendor"
              >
                {t("pages:vendors.add.submitVendor")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AdminVendorDialog({ vendor, onDone }: { vendor?: VendorWithReviews; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const isEdit = !!vendor;

  const [name, setName] = useState(vendor?.name ?? "");
  const [category, setCategory] = useState(vendor?.category ?? "");
  const [description, setDescription] = useState(vendor?.description ?? "");
  const [websiteUrl, setWebsiteUrl] = useState(vendor?.websiteUrl ?? "");
  const [sizeFit, setSizeFit] = useState(vendor?.sizeFit ?? "");
  const [affordability, setAffordability] = useState(vendor?.affordabilityScore ?? 5);
  const [technology, setTechnology] = useState(vendor?.technologyScore ?? 5);
  const [value, setValue] = useState(vendor?.valueScore ?? 5);
  const [support, setSupport] = useState(vendor?.supportScore ?? 5);
  const [sizeFitScore, setSizeFitScore] = useState(vendor?.sizeFitScore ?? 5);

  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      const data = {
        name: name.trim(),
        category,
        description: description.trim() || null,
        websiteUrl: websiteUrl.trim() || null,
        sizeFit: sizeFit.trim() || null,
        affordabilityScore: affordability,
        technologyScore: technology,
        valueScore: value,
        supportScore: support,
        sizeFitScore,
      };
      if (isEdit) return apiRequest("PATCH", `/api/admin/vendors/${vendor!.id}`, data);
      return apiRequest("POST", "/api/admin/vendors", data);
    },
    onSuccess: () => {
      toast({ title: isEdit ? t("pages:vendors.admin.vendorUpdated") : t("pages:vendors.admin.vendorCreated") });
      qc.invalidateQueries({ queryKey: ["/api/vendors"] });
      setOpen(false);
      onDone();
    },
    onError: (e: any) => toast({ title: t("common:error"), description: e.message, variant: "destructive" }),
  });

  const scoreFields = [
    { key: "affordability", label: t("pages:vendors.dimensions.affordability"), val: affordability, set: setAffordability },
    { key: "technology", label: t("pages:vendors.dimensions.technology"), val: technology, set: setTechnology },
    { key: "value", label: t("pages:vendors.dimensions.value"), val: value, set: setValue },
    { key: "support", label: t("pages:vendors.dimensions.support"), val: support, set: setSupport },
    { key: "size-fit-score", label: t("pages:vendors.dimensions.sizeFitScore"), val: sizeFitScore, set: setSizeFitScore },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button size="icon" variant="ghost" data-testid={`button-edit-vendor-${vendor!.id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        ) : (
          <Button variant="outline" data-testid="button-admin-add-vendor">
            <Plus className="w-4 h-4 mr-2" />
            {t("pages:vendors.admin.addVendor")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("pages:vendors.admin.editVendor") : t("pages:vendors.admin.addVendor")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("pages:vendors.admin.nameLabel")}</Label>
              <Input data-testid="input-admin-vendor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("pages:vendors.admin.vendorNamePlaceholder")} />
            </div>
            <div>
              <Label>{t("pages:vendors.admin.categoryLabel")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="select-admin-vendor-category">
                  <SelectValue placeholder={t("pages:vendors.admin.selectShort")} />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter((c) => c !== "All").map((c) => (
                    <SelectItem key={c} value={c}>{t(`pages:vendors.categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("pages:vendors.admin.websiteUrlLabel")}</Label>
            <Input data-testid="input-admin-vendor-url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder={t("pages:vendors.admin.urlPlaceholder")} />
          </div>
          <div>
            <Label>{t("pages:vendors.admin.sizeFitLabel")}</Label>
            <Input data-testid="input-admin-vendor-sizefit" value={sizeFit} onChange={(e) => setSizeFit(e.target.value)} placeholder={t("pages:vendors.admin.sizeFitPlaceholder")} />
          </div>
          <div>
            <Label>{t("pages:vendors.admin.descriptionLabel")}</Label>
            <Textarea data-testid="input-admin-vendor-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium">{t("pages:vendors.dimensionScoresRange")}</p>
            {scoreFields.map(({ key, label, val, set }) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-28 text-sm">{label}</span>
                <Slider
                  min={1} max={10} step={1}
                  value={[val]}
                  onValueChange={([v]) => set(v)}
                  className="flex-1"
                  data-testid={`slider-${key}`}
                />
                <span className="w-6 text-sm font-medium text-right">{val}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="button-cancel-edit-vendor">{t("common:cancel")}</Button>
            <Button
              onClick={() => mutation.mutate()}
              disabled={!name.trim() || !category || mutation.isPending}
              data-testid="button-save-vendor"
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? t("pages:vendors.admin.saveChanges") : t("pages:vendors.admin.createVendor")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorDetailDialog({ vendor, isAdmin, onReviewOpen, currentUserId }: {
  vendor: VendorWithReviews;
  isAdmin: boolean;
  onReviewOpen: (v: VendorWithReviews) => void;
  currentUserId: string;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const [open, setOpen] = useState(false);
  const myReview = vendor.myReview;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="text-xs"
          data-testid={`button-view-vendor-${vendor.id}`}
        >
          {t("pages:vendors.viewDetails")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-left" data-testid={`text-detail-name-${vendor.id}`}>{vendor.name}</DialogTitle>
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge variant="secondary" className="text-xs">{vendor.category}</Badge>
                {vendor.sizeFit && <Badge variant="outline" className="text-xs">{vendor.sizeFit}</Badge>}
                {vendor.isCommunityAdded && !vendor.isApproved && (
                  <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
                    {t("pages:vendors.communityAdded")}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end shrink-0">
              <div className="text-2xl font-bold text-primary leading-none">{vendor.compositeScore.toFixed(1)}</div>
              <div className="text-xs text-muted-foreground">/ 10</div>
              {vendor.averageStars != null && (
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs">{vendor.averageStars.toFixed(1)}</span>
                  <span className="text-xs text-muted-foreground">({vendor.reviews.length})</span>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {vendor.description && (
            <div>
              <p className="text-sm font-medium mb-1">{t("pages:vendors.about")}</p>
              <p className="text-sm text-muted-foreground" data-testid={`text-detail-description-${vendor.id}`}>
                {vendor.description}
              </p>
            </div>
          )}

          {vendor.websiteUrl && (
            <a
              href={vendor.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-primary hover:underline w-fit"
              data-testid={`link-detail-website-${vendor.id}`}
            >
              <ExternalLink className="w-4 h-4" />
              {vendor.websiteUrl}
            </a>
          )}

          <div>
            <p className="text-sm font-medium mb-2">{t("pages:vendors.dimensionScores")}</p>
            <div className="space-y-1.5">
              <ScoreBar label={t("pages:vendors.dimensions.affordability")} value={vendor.affordabilityScore ?? 5} />
              <ScoreBar label={t("pages:vendors.dimensions.technology")} value={vendor.technologyScore ?? 5} />
              <ScoreBar label={t("pages:vendors.dimensions.value")} value={vendor.valueScore ?? 5} />
              <ScoreBar label={t("pages:vendors.dimensions.support")} value={vendor.supportScore ?? 5} />
              <ScoreBar label={t("pages:vendors.dimensions.sizeFit")} value={vendor.sizeFitScore ?? 5} />
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium">{t("pages:vendors.communityReviews", { count: vendor.reviews.length })}</p>
              <Button
                size="sm"
                variant={myReview ? "outline" : "default"}
                className="text-xs"
                onClick={() => {
                  setOpen(false);
                  onReviewOpen(vendor);
                }}
                data-testid={`button-rate-vendor-${vendor.id}`}
              >
                <Star className="w-3 h-3 mr-1" />
                {myReview ? t("pages:vendors.editMyReview") : t("pages:vendors.rateThisVendor")}
              </Button>
            </div>

            {vendor.reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("pages:vendors.noReviewsYet")}</p>
            ) : (
              <div className="space-y-3" data-testid={`reviews-section-${vendor.id}`}>
                {vendor.reviews.map((r) => (
                  <div key={r.id} className="space-y-1 border-b last:border-0 pb-3 last:pb-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2">
                      <StarRating value={r.stars} readonly />
                      <span className="text-xs font-medium">{r.reviewerName}</span>
                      {r.userId === currentUserId && (
                        <Badge variant="outline" className="text-xs">{t("pages:vendors.you")}</Badge>
                      )}
                    </div>
                    {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorCard({ vendor, isAdmin, onReviewOpen, currentUserId, onExternalLinkClick }: {
  vendor: VendorWithReviews;
  isAdmin: boolean;
  onReviewOpen: (v: VendorWithReviews) => void;
  currentUserId: string;
  onExternalLinkClick?: (vendorId: string) => void;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/admin/vendors/${vendor.id}`),
    onSuccess: () => {
      toast({ title: t("pages:vendors.toast.vendorDeleted") });
      qc.invalidateQueries({ queryKey: ["/api/vendors"] });
    },
    onError: (e: any) => toast({ title: t("common:error"), description: e.message, variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/admin/vendors/${vendor.id}/approve`),
    onSuccess: () => {
      toast({ title: t("pages:vendors.toast.vendorApproved") });
      qc.invalidateQueries({ queryKey: ["/api/vendors"] });
    },
    onError: (e: any) => toast({ title: t("common:error"), description: e.message, variant: "destructive" }),
  });

  const myReview = vendor.myReview;

  return (
    <Card
      data-testid={`card-vendor-${vendor.id}`}
      className="flex flex-col"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm leading-tight truncate" data-testid={`text-vendor-name-${vendor.id}`}>{vendor.name}</h3>
              <div className="flex flex-wrap gap-1 mt-1">
                <Badge variant="secondary" className="text-xs" data-testid={`badge-category-${vendor.id}`}>{vendor.category}</Badge>
                {vendor.sizeFit && <Badge variant="outline" className="text-xs" data-testid={`badge-sizefit-${vendor.id}`}>{vendor.sizeFit}</Badge>}
                {vendor.isCommunityAdded && !vendor.isApproved && (
                  <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100" data-testid={`badge-community-${vendor.id}`}>
                    {t("pages:vendors.communityAdded")}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <div className="text-2xl font-bold text-primary leading-none" data-testid={`text-score-${vendor.id}`}>
              {vendor.compositeScore.toFixed(1)}
            </div>
            <div className="text-xs text-muted-foreground">/ 10</div>
            {vendor.averageStars != null && (
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs" data-testid={`text-avg-stars-${vendor.id}`}>{vendor.averageStars.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground">({vendor.reviews.length})</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 flex flex-col flex-1 gap-3">
        {vendor.description && (
          <p className="text-xs text-muted-foreground line-clamp-2" data-testid={`text-description-${vendor.id}`}>
            {vendor.description}
          </p>
        )}

        <div className="space-y-1">
          <ScoreBar label={t("pages:vendors.dimensions.affordability")} value={vendor.affordabilityScore ?? 5} />
          <ScoreBar label={t("pages:vendors.dimensions.technology")} value={vendor.technologyScore ?? 5} />
          <ScoreBar label={t("pages:vendors.dimensions.value")} value={vendor.valueScore ?? 5} />
          <ScoreBar label={t("pages:vendors.dimensions.support")} value={vendor.supportScore ?? 5} />
          <ScoreBar label={t("pages:vendors.dimensions.sizeFit")} value={vendor.sizeFitScore ?? 5} />
        </div>

        {vendor.websiteUrl && (
          <a
            href={vendor.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-primary hover:underline w-fit"
            data-testid={`link-website-${vendor.id}`}
            onClick={() => onExternalLinkClick?.(vendor.id)}
          >
            <ExternalLink className="w-3 h-3" />
            {t("pages:vendors.visitWebsite")}
          </a>
        )}

        <div className="flex gap-2 flex-wrap mt-auto pt-1">
          <VendorDetailDialog
            vendor={vendor}
            isAdmin={isAdmin}
            onReviewOpen={onReviewOpen}
            currentUserId={currentUserId}
          />

          <Button
            size="sm"
            variant={myReview ? "outline" : "default"}
            className="text-xs"
            onClick={() => onReviewOpen(vendor)}
            data-testid={`button-rate-vendor-${vendor.id}`}
          >
            <Star className="w-3 h-3 mr-1" />
            {myReview ? t("pages:vendors.editMyReview") : t("pages:vendors.rateThisVendor")}
          </Button>

          {isAdmin && (
            <div className="flex gap-1 ml-auto">
              {vendor.isCommunityAdded && !vendor.isApproved && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="w-7 h-7 text-green-600"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isPending}
                  data-testid={`button-approve-vendor-${vendor.id}`}
                  title={t("pages:vendors.approveTitle")}
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
              )}
              <AdminVendorDialog vendor={vendor} onDone={() => {}} />
              <Button
                size="icon"
                variant="ghost"
                className="w-7 h-7 text-destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                data-testid={`button-delete-vendor-${vendor.id}`}
                title={t("pages:vendors.deleteTitle")}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function useVendorInteraction() {
  const emit = useCallback(async (eventType: string, vendorId?: string, metadata?: Record<string, string>) => {
    try {
      await apiRequest("POST", "/api/vendor-interactions", { eventType, vendorId, metadata });
    } catch {
      // fire-and-forget, ignore errors silently
    }
  }, []);
  return emit;
}

export default function VendorDirectoryPage() {
  const { t } = useTranslation(["pages", "common"]);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("composite");
  const [reviewVendor, setReviewVendor] = useState<VendorWithReviews | null>(null);
  const [showAll, setShowAll] = useState(false);
  const emitInteraction = useVendorInteraction();
  const viewEmitted = useRef(false);

  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/me"], retry: false });
  const isAdmin = user?.role === "owner" || user?.role === "admin";

  // Emit directory_view once on mount (only for authenticated dealers)
  useEffect(() => {
    if (!viewEmitted.current && user?.id) {
      viewEmitted.current = true;
      emitInteraction("directory_view");
    }
  }, [user?.id, emitInteraction]);

  // Emit category_filter when category changes (not on initial load)
  const prevCategory = useRef<string | null>(null);
  useEffect(() => {
    if (prevCategory.current !== null && prevCategory.current !== category && user?.id) {
      emitInteraction("vendor_filter", undefined, { category });
    }
    prevCategory.current = category;
  }, [category, user?.id, emitInteraction]);

  const queryCategory = category === "All" ? undefined : category;
  const { data: vendors = [], isLoading } = useQuery<VendorWithReviews[]>({
    queryKey: ["/api/vendors", { category: queryCategory, sort }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (queryCategory) params.set("category", queryCategory);
      if (sort) params.set("sort", sort);
      const res = await fetch(`/api/vendors?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch vendors");
      return res.json();
    },
  });

  const visibleVendors = showAll || isAdmin
    ? vendors
    : vendors.filter((v) => v.isApproved);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" data-testid="heading-vendor-directory">{t("pages:vendors.pageTitle")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("pages:vendors.pageSubtitle")}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {isAdmin && <AdminVendorDialog onDone={() => {}} />}
          <AddVendorDialog onAdded={() => {}} />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <Tabs value={category} onValueChange={setCategory}>
            <TabsList className="flex-wrap h-auto gap-1" data-testid="tabs-category">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c} value={c} className="text-xs" data-testid={`tab-category-${c.toLowerCase()}`}>
                  {t(`pages:vendors.categories.${c}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-sm text-muted-foreground">{t("pages:vendors.sortBy")}</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40" data-testid="select-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>{t(`pages:vendors.sort.${o.value}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" data-testid={`skeleton-vendor-${i}`} />
          ))}
        </div>
      ) : visibleVendors.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="text-empty-vendors">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">{t("pages:vendors.noVendorsFound")}</p>
          <p className="text-sm mt-1">{t("pages:vendors.beFirstToAdd")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleVendors.map((v) => (
            <VendorCard
              key={v.id}
              vendor={v}
              isAdmin={isAdmin}
              onReviewOpen={setReviewVendor}
              currentUserId={user?.id ?? ""}
              onExternalLinkClick={(vendorId) => emitInteraction("external_link_click", vendorId)}
            />
          ))}
        </div>
      )}

      {!showAll && !isAdmin && vendors.some((v) => !v.isApproved) && (
        <div className="text-center">
          <Button variant="ghost" size="sm" onClick={() => setShowAll(true)} data-testid="button-show-pending">
            {t("pages:vendors.showPending")}
          </Button>
        </div>
      )}

      <Dialog open={!!reviewVendor} onOpenChange={(o) => !o && setReviewVendor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("pages:vendors.rateVendorTitle", { name: reviewVendor?.name })}</DialogTitle>
          </DialogHeader>
          {reviewVendor && (
            <ReviewForm
              vendorId={reviewVendor.id}
              existingReview={reviewVendor.myReview}
              onClose={() => setReviewVendor(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
